import React, { useEffect } from "react"
import { useSelector } from "react-redux"
import * as CKEditorReact from "@ckeditor/ckeditor5-react"

import { MtDriveFile } from "server/models"
import {
    StoreType,
    UserData,
    UploadFile,
    getFile,
} from "client/store"
import { StorageType } from "client/utils"

import CKEditorCustom from "./ckeditor"


let projectId: number = null
let storage: StorageType = null
let userData: UserData = null
let onUploadFunction: (newFile: MtDriveFile) => void


class UploadAdapter {
    private loader: any

    constructor(loader: any) {
        // the file loader instance to use during the upload
        this.loader = loader
    }

    // starts the upload process
    upload() {
        return this.loader.file
        .then((file: File) => new Promise((resolve, reject) => {
            UploadFile({
                file,
                storage,
                userData,
                projectId,
                targetFolderId: null,
                callbackSuccess: (newFile) => {
                    const url = getFile(newFile, newFile.versions[0]).preview
                    resolve({ default: url }) // inserts image to the editor
                    if (onUploadFunction) onUploadFunction(newFile)
                },
                callbackError: reject,
            })
        }))
    }
}

function UploadAdapterPlugin(editor: any) {
    editor.plugins.get("FileRepository").createUploadAdapter = (loader: any) => new UploadAdapter(loader)
}


const createConfig = (imagePlugin: boolean) => {
    const items = [
        "heading",
        "|",
        "fontFamily",
        "fontSize",
        "fontBackgroundColor",
        "fontColor",
        "|",
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "|",
        "subscript",
        "superscript",
        "removeFormat",
        "|",
        "link",
    ]
    if (imagePlugin) items.push("imageInsert")
    items.push(
        "|",
        "bulletedList",
        "numberedList",
        "|",
        "code",
        "codeBlock",
        "|",
        "alignment",
        "outdent",
        "indent",
        "horizontalLine",
        "|",
        "blockQuote",
        "insertTable",
        "|",
        "undo",
        "redo",
    )

    return {
        toolbar: { items },
        language: "en",
        extraPlugins: imagePlugin ? [UploadAdapterPlugin] : [],
    }
}


export interface CKEditorProps {
    data: any
    isReadOnly?: boolean
    useImagePlugin?: boolean
    onInit?: (editor?: any) => void
    onUpload?: (newFile: MtDriveFile) => void
    onChange?: (data: string) => void
}

export const CKEditor = ({
    data = null,
    isReadOnly = false,
    useImagePlugin = false,
    onInit = null,
    onUpload = null,
    onChange = null,
}: CKEditorProps) => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const accountSettings = useSelector((state: StoreType) => state.system.accountSettings)
    const systemUserData = useSelector((state: StoreType) => state.system.userData)

    useEffect(() => {
        projectId = project ? project.id : null
        storage = accountSettings.drive.defaultStorage
        userData = systemUserData
        onUploadFunction = onUpload
    }, [project, accountSettings, systemUserData])

    return (
        <CKEditorReact.CKEditor
            editor={CKEditorCustom}
            config={createConfig(useImagePlugin)}
            data={data}
            onReady={(editor: any) => {
                if (!editor) return
                const toolbarElement = editor.ui.view.toolbar.element
                if (isReadOnly) {
                    editor.enableReadOnlyMode("my-lock-id")
                    toolbarElement.style.display = "none"
                } else {
                    editor.disableReadOnlyMode("my-lock-id")
                    toolbarElement.style.display = "flex"
                }

                if (onInit) onInit(editor)
            }}
            onChange={(event: any, editor: any) => {
                if (!editor || !onChange) return
                onChange(editor.getData())
            }}
        />
    )
}
